/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useEffect} from 'react'
import clsx from 'clsx'
import {useTypedSelector} from 'helpers/reducers'
import {useTranslation} from 'react-i18next'
import {packageItemUpsellPersonType, sortPackageItemsByPosition,} from 'utils/utility'
import {getCreditAndUpsellItems, isCreditPackageItem} from 'utils/offers/addItemToCalculationUtils'
import {useClearUpsells} from 'utils/packageUtils'
import {toast} from 'utils/toastUtils'
import {useGetGroupedUpsells} from 'utils/upsellsUtils'
import UpsellPriceList from 'pages/Front/Booking/components/PackageUpsell/UpsellPriceList'
import {Offer} from 'types/entities/Offer'
import useSellerTimeslots from 'utils/hooks/useSellerTimeslots'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'

import UiSectionHeader from 'components/Ui/UiSectionHeader'
import {ArrowUturnLeftIcon} from '@heroicons/react/24/solid'
import {getSelectedBookingCredits} from 'utils/booking/creditItems'


interface BookingUpsellsProps {
    backHandler: () => void
    finishHandler: (offer?: Offer | undefined) => void
    isCalculationMode?: boolean
    hideFooter?: boolean
    isCompact?: boolean
}

const SellerUpsells = (props: BookingUpsellsProps) => {
    const availablePackageItems = useTypedSelector(
        (state) => state.bookingState.bookingProcess?.availablePackageItems
    )
    const bookingProcess = useTypedSelector(
        (state) => state.bookingState.bookingProcess
    )
    const bookingFilter = bookingProcess.filter
    const packageItemsWithoutGroupOptions = getCreditAndUpsellItems(availablePackageItems, bookingFilter.category)
    const selectedCredits = getSelectedBookingCredits(bookingProcess)
    const packageCart = useTypedSelector(
        (state) => state.bookingState.bookingProcess.packageCart
    )
    const persons = +bookingFilter.adults + +bookingFilter.children
    const selectedUpsells = useTypedSelector(
        (state) => state.bookingState.bookingProcess.upsell
    )

    const selectedOffer = useTypedSelector(
        (state) => state.offersState.selectedOffer
    )

    const clearPackagesHook = useClearUpsells(bookingProcess)

    const seller_id =
        useTypedSelector((state) => state.sessionState.session?.uid) || 1

    const { createOfferWithUpsells, isLoading } = useSellerTimeslots(
        () => { },
        null,
        [null, null, null],
        (offer) => props.finishHandler(offer),
        seller_id,
        props.isCalculationMode
    ) as any

    const { t } = useTranslation()
    const sortedPackageItemsList = packageItemsWithoutGroupOptions
        ? sortPackageItemsByPosition(packageItemsWithoutGroupOptions)
        : []
    const packageItemsList =
        sortedPackageItemsList.length > 0
            ? sortedPackageItemsList
                .filter((packageItem) => {
                    return packageItemUpsellPersonType(
                        bookingFilter,
                        packageItem.person_type
                    )
                })
                .filter((packageItem) => {
                    if (isCreditPackageItem(packageItem) && Number(bookingFilter.adults) === 0) {
                        return false
                    }

                    if (packageItem.extra?.groupName === 'Competitions') {
                        return true
                    }

                    if (packageItem.minimum_persons_count === 0) return true
                    if (packageItem.minimum_persons_count > 0) {
                        return persons >= packageItem.minimum_persons_count
                    }
                })
                .filter((packageItem) => packageItem.is_active == 1)
            : []

    // Group items by variant_id
    const { sortedByGroupNames } = useGetGroupedUpsells(packageItemsList)

    const submitPackageUpsells = (): void => {
        if (!packageCart) {
            toast.error(t('No packageCart found'))
            return
        }

        if (!bookingProcess.selectedTimeslot) {
            toast.error(t('No timeslot selected'))
            return
        }

        if (!bookingFilter.category) {
            toast.error(t('No category selected'))
            return
        }

        if (selectedOffer) {
            createOfferWithUpsells(selectedOffer, true)
        } else {
            toast.error(t('No selected offer found'))
        }
    }

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells()
    }, [selectedUpsells])

    return (
        <div
            className={clsx('SellerUpsells mx-auto w-full flex flex-col gap-4')}
        >
            {packageItemsList.length > 0 && (
                <>
                    <UiSectionHeader title={t('Please select your extras')} />
                    <UpsellPriceList
                        groups={sortedByGroupNames}
                        isCompact={props.isCompact}
                    />
                </>
            )}
            {packageItemsList.length === 0 && (
                <div className={clsx('text-center')}>
                    {t('No upsells found for selected package.')}
                </div>
            )}
            {!props.hideFooter && (
                <div
                    className={clsx(
                        'flex flex-row mt-4 w-full text-white justify-between gap-4'
                    )}
                >
                    <UiButton
                        className={clsx('BookingFooterStepsBack')}
                        icon={
                            <ArrowUturnLeftIcon className={clsx('w-5 h-5')} />
                        }
                        size={UiButtonSize.Medium}
                        style={UiButtonStyle.Gray}
                        title={t('Back')}
                        onClick={props.backHandler}
                    />
                    <UiButton
                        disabled={isLoading || !bookingProcess.selectedTimeslot}
                        id="ButtonSubmitUpsells"
                        size={UiButtonSize.Medium}
                        style={UiButtonStyle.Primary}
                        title={
                            selectedUpsells.length === 0 && selectedCredits.length === 0
                                ? t('Continue without upsells')
                                : t('Continue')
                        }
                        onClick={submitPackageUpsells}
                    />
                </div>
            )}
        </div>
    )
}

export default SellerUpsells
